// src/data/worksheets/index.js
//
// Registry of interactive worksheets / checklists rendered by
// components/worksheets/WorksheetRunner.jsx and stored per user in
// users/{uid}/private_journal/{worksheetId} (self-only — see utils/journalStore.js).
//
// A topic resource points at one with `{ type: 'Worksheet', worksheetId }`.
//
// SCHEMA
//   { id, topicId, kind: 'checklist' | 'worksheet', title, intro?, outro?, supportNote?,
//     sections: [ { id, title, description?, prompts: [Prompt] } ] }
//
// Prompt types (answers are stored under answers[prompt.id]):
//   checklist { label, items: [{ id, label, detail? }] }     -> { [itemId]: true }
//   scale     { label, help?, min, max, minLabel?, maxLabel? } -> number
//   text      { label, help?, multiline? }                     -> string
//   list      { label, help?, count, itemLabels? }             -> string[]  (itemLabels replace the 1..n numbering)
//   multi     { label, help?, options: [{ id, label, flag? }], other? } -> { [optId]: true, other?: string }
//             (flag: 'support' surfaces the Talk to Someone outlet when ticked)
//   contacts  { label, help?, count, fields: ['name','phone','email'] } -> object[]
//   table     { label, columns: [{ id, label, kind: 'date'|'number'|'text', min?, max? }], maxRows?,
//               chart?: { x, series: [{ id, label }] } }     -> object[] (one per row)
//
// Future (left open on purpose): a Cloud Function can aggregate completion
// counts / scale answers across an org under the usual min-cohort suppression —
// the docs carry `kind`, `topicId`, `progress`, `completedAt` and numeric
// answers for exactly that. Nothing client-side reads other people's journals.

import { ANXIETY_WORKSHEETS } from './anxiety';
import { DEPRESSION_WORKSHEETS } from './depression';
import { ADHD_WORKSHEETS } from './adhd';
import { ADDICTIONS_WORKSHEETS } from './addictions';
import { EATING_WORKSHEETS } from './eating';
import { GAMBLING_WORKSHEETS } from './gambling';
import { TRAUMA_WORKSHEETS } from './trauma';
import { FOCUS_WORKSHEETS } from './focus';
import { RESILIENCE_WORKSHEETS } from './resilience';
import { MOTIVATION_WORKSHEETS } from './motivation';
import { CONFIDENCE_WORKSHEETS } from './confidence';
import { DISCIPLINE_WORKSHEETS } from './discipline';
import { AWARENESS_WORKSHEETS } from './awareness';
import { ADAPTABILITY_WORKSHEETS } from './adaptability';
import { SLEEP_WORKSHEETS } from './sleep';
import { NUTRITION_WORKSHEETS } from './nutrition';
import { INJURY_WORKSHEETS } from './injury';
import { RESPECT_BELONGING_WORKSHEETS } from './respectBelonging';

export const WORKSHEETS = Object.fromEntries(
    [
        ...ANXIETY_WORKSHEETS, ...DEPRESSION_WORKSHEETS, ...ADHD_WORKSHEETS,
        ...ADDICTIONS_WORKSHEETS, ...EATING_WORKSHEETS, ...GAMBLING_WORKSHEETS, ...TRAUMA_WORKSHEETS,
        ...FOCUS_WORKSHEETS, ...RESILIENCE_WORKSHEETS, ...MOTIVATION_WORKSHEETS, ...CONFIDENCE_WORKSHEETS,
        ...DISCIPLINE_WORKSHEETS, ...AWARENESS_WORKSHEETS, ...ADAPTABILITY_WORKSHEETS,
        ...SLEEP_WORKSHEETS, ...NUTRITION_WORKSHEETS, ...INJURY_WORKSHEETS,
        ...RESPECT_BELONGING_WORKSHEETS
    ].map(w => [w.id, w])
);

// The module TopicView appends to a topic that has worksheets in this registry
// (no content.js entry needed — covers future performance-side sheets too).
export const journalModuleFor = (topicId) => {
    const list = worksheetsForTopic(topicId);
    if (list.length === 0) return null;
    return {
        id: 'tools_journal',
        title: 'Checklist & Journal',
        resources: list.map(w => ({
            id: `ws_${w.id}`,
            title: w.title,
            type: 'Worksheet',
            worksheetId: w.id,
            content: w.kind === 'checklist' ? 'Interactive checklist — saves privately to your journal.' : 'Interactive worksheet — saves privately to your journal.'
        }))
    };
};

export const getWorksheet = (id) => WORKSHEETS[id] || null;

export const worksheetsForTopic = (topicId) =>
    Object.values(WORKSHEETS).filter(w => w.topicId === topicId);

// Count answerable units (checklist items count individually) and how many
// have an answer — the progress shown on the runner and in My Journal.
export const progressFor = (def, answers = {}) => {
    let total = 0, answered = 0;
    (def?.sections || []).forEach(s => (s.prompts || []).forEach(p => {
        const a = answers[p.id];
        switch (p.type) {
            case 'checklist':
                (p.items || []).forEach(it => { total++; if (a && a[it.id]) answered++; });
                break;
            case 'list':
                total++; if (Array.isArray(a) && a.some(v => String(v || '').trim())) answered++;
                break;
            case 'multi':
                total++; if (a && Object.entries(a).some(([k, v]) => k === 'other' ? String(v || '').trim() : v)) answered++;
                break;
            case 'contacts':
                total++; if (Array.isArray(a) && a.some(row => row && Object.values(row).some(v => String(v || '').trim()))) answered++;
                break;
            case 'table':
                total++; if (Array.isArray(a) && a.some(row => row && Object.values(row).some(v => String(v ?? '').trim()))) answered++;
                break;
            case 'scale':
                total++; if (typeof a === 'number') answered++;
                break;
            default:
                total++; if (String(a || '').trim()) answered++;
        }
    }));
    return { total, answered, pct: total ? Math.round((answered / total) * 100) : 0 };
};
